import hash from 'object-hash';
import { find, sortBy, keys, includes } from 'lodash-es';
import { SECURITY_GROUPS_RETRIEVED } from '../security/security.action';
import { REQUEST_USER_ACCOUNTS_SUCCESS } from '../user/user.actions';

import { getLastSeenOptionByMinMax } from '../../components/last-seen-filter/last-seen-filter.models';

//--------------
// Constants
//--------------
import {
  SEARCH_UPDATED,
  SEARCH_BEGIN,
  SEARCH_SUCCESS,
  SEARCH_FAILURE,
  SEARCH_INITIATED,
  FACET_SELECTED,
  FACET_SELECTED_EXCLUSIVE,
  FACET_DESELECTED,
  FACET_DESELECTED_EXCLUSIVE,
  SET_SELECTED_FACETS,
  SET_SELECTED_TAG_FILTERS,
  TAG_FILTERS_UPDATED,
  TAG_FILTER_ADDED,
  TAG_FILTER_REMOVED,
  QUERY_ADDED,
  QUERY_REMOVED,
  SORT_UPDATED,
  ALERTS_FACET_TYPE,
  SECURITY_GROUPS_FACET_TYPE,
  MAINTENANCE_STATUS_FACET_TYPE,
  COMMUNICATED_AT_FACET_TYPE,
  SEARCH_REQUESTED,
  SEARCH_RESET,
  SEARCH_EXPORT,
  SEARCH_EXPORT_SUCCESS,
  SEARCH_EXPORT_FAILURE,
  ACCOUNT_ADDED,
  ACCOUNT_REMOVED,
  UPDATE_SEARCH_PREREQUISITES,
  LAST_SEEN_UPDATED,
} from './search.action';

import { INITIAL_STATE } from './search.state';

const tags = {
  [ALERTS_FACET_TYPE]: (priority) => `onei3:alert:${priority}`,
  [SECURITY_GROUPS_FACET_TYPE]: (group) => `onei3:security:group:${group}`,
  [MAINTENANCE_STATUS_FACET_TYPE]: (plan) => `onei3:maintenance:${plan}`,
};

const setSelected = (item, selectedItems) => {
  if (selectedItems) {
    selectedItems.forEach((selectedItem) => {
      item._selected = selectedItem.id === item.id;
    });
  }
};

const applySelected = (item, selectedItems = [], facetType) => {
  //override Selected
  item._selected = !!selectedItems.find((selected) => selected.id === item.id);
  //set the tag for alerts,maintenances,sec
  if (item._selected && facetType !== COMMUNICATED_AT_FACET_TYPE) {
    item.tag = tags[facetType](item.id);
  }
  return item;
};

const applySavedLastSeen = (saved) => {
  if (saved) {
    const lastSeen = getLastSeenOptionByMinMax(saved.min, saved.max);
    return lastSeen ? { ...lastSeen, _selected: true } : null;
  }
};

export const SearchReducer = (state = INITIAL_STATE, { type, payload }) => {
  switch (type) {
    case SEARCH_UPDATED:
      return {
        ...state,
        hash: createSearchHash(state),
      };

    case SORT_UPDATED: {
      const direction =
        state.sort.name === payload && state.sort.direction === 'asc' ? 'desc' : 'asc';
      return {
        ...state,
        sort: {
          name: payload,
          direction,
        },
      };
    }
    case QUERY_ADDED: {
      if (!payload || includes(state.query, payload)) {
        return state;
      }

      const newQuery = [...state.query, payload];
      return {
        ...state,
        query: newQuery,
      };
    }

    case QUERY_REMOVED: {
      const query = [...state.query];
      return {
        ...state,
        query: query.filter((item) => item !== payload),
      };
    }

    case SEARCH_UPDATED: // eslint-disable-line no-duplicate-case
    case SEARCH_BEGIN:
    case SEARCH_REQUESTED:
      return {
        ...state,
        inProgress: true,
      };

    case SEARCH_INITIATED:
      return {
        ...state,
        inProgress: true,
        query: payload.query,
        securityGroups: state.securityGroups.map((item) =>
          applySelected(item, payload.securityGroups, SECURITY_GROUPS_FACET_TYPE),
        ),
        alerts: state.alerts.map((item) => applySelected(item, payload.alerts, ALERTS_FACET_TYPE)),
        maintenance: state.maintenance.map((item) =>
          applySelected(item, payload.maintenance, MAINTENANCE_STATUS_FACET_TYPE),
        ),
        lastCommunicated: state.lastCommunicated.map((item) =>
          applySelected(item, payload.lastCommunicated, COMMUNICATED_AT_FACET_TYPE),
        ),
        tagFilters: payload.tagFilters || {},
        accounts: state.accounts.map((item) => ({
          ...item,
          _selected: !!(payload.accounts || []).find(
            (selected) => '' + selected.id === '' + item.id,
          ),
        })),
        lastSeen: (payload.lastSeen || []).map((ls) => applySavedLastSeen(ls)),
      };

    case SEARCH_SUCCESS:
      return {
        ...state,
        inProgress: false,
        initial: false,
        cursor: payload.cursor,
        error: '',
      };

    case SEARCH_FAILURE:
      return {
        ...state,
        inProgress: false,
        error: payload,
      };

    case SEARCH_EXPORT:
      return {
        ...state,
        exportInProgress: true,
      };

    case SEARCH_EXPORT_SUCCESS:
    case SEARCH_EXPORT_FAILURE:
      return {
        ...state,
        exportInProgress: false,
      };

    case FACET_DESELECTED:
    case FACET_DESELECTED_EXCLUSIVE:
    case FACET_SELECTED:
    case FACET_SELECTED_EXCLUSIVE:
      return reduceFacets(state, type, payload);

    case SET_SELECTED_FACETS: {
      const { alerts, maintenance, lastCommunicated, securityGroups } = state;

      alerts.forEach((item) => setSelected(item, payload.alerts));
      maintenance.forEach((item) => setSelected(item, payload.maintenance));
      lastCommunicated.forEach((item) => setSelected(item, payload.lastCommunicated));
      securityGroups.forEach((item) => setSelected(item, payload.securityGroups));

      return {
        ...state,
        alerts,
        maintenance,
        lastCommunicated,
        securityGroups,
      };
    }

    case SET_SELECTED_TAG_FILTERS: {
      const tagFilters = payload;

      return {
        ...state,
        tagFilters,
      };
    }

    case TAG_FILTERS_UPDATED:
      return {
        ...state,
        tagFilters: payload,
      };

    case TAG_FILTER_ADDED: {
      const tagFilters = { ...state.tagFilters };
      tagFilters[payload.key] = payload.value;
      return {
        ...state,
        tagFilters: tagFilters,
      };
    }

    case TAG_FILTER_REMOVED: {
      const key = payload.key ? payload.key : payload;
      let { [key]: omit, ...rest } = state.tagFilters; // eslint-disable-line no-unused-vars
      return {
        ...state,
        tagFilters: rest,
      };
    }

    case SECURITY_GROUPS_RETRIEVED:
      return updateSecurityGroups(state, payload);

    case SEARCH_RESET: {
      const newState = { ...state };
      newState.query = [];
      newState.tagFilters = {};
      newState.lastCommunicated = newState.lastCommunicated.map((value) => clearSelected(value));
      newState.maintenance = newState.maintenance.map((value) => clearSelected(value));
      newState.alerts = newState.alerts.map((value) => clearSelected(value));
      newState.securityGroups = newState.securityGroups.map((value) => clearSelected(value));
      newState.accounts = newState.accounts.map((value) => clearSelected(value));
      newState.lastSeen = [];
      return newState;
    }

    case REQUEST_USER_ACCOUNTS_SUCCESS: {
      const { currentAccount } = payload;
      const { accounts } = state;
      const selectedAccounts = (accounts || []).filter((acc) => acc.selected).map((acc) => acc.id);
      //add label prop that required for filter input to work,
      //override master to get clear result when filtering the list
      //sync with selected
      let userAccounts = [currentAccount, ...(currentAccount.accounts || [])].map((account) => ({
        id: account.id,
        name: account.name,
        label: account.name,
        _selected: selectedAccounts.includes(account.id),
      }));
      return { ...state, accounts: sortBy(userAccounts, 'label') };
    }

    case ACCOUNT_ADDED: {
      return {
        ...state,
        accounts: state.accounts.map((acc) => {
          if (acc.id === payload.id) {
            acc._selected = true;
          }
          return acc;
        }),
      };
    }

    case ACCOUNT_REMOVED: {
      return {
        ...state,
        accounts: state.accounts.map((acc) => {
          if (acc.id === payload.id) {
            acc._selected = false;
          }
          return acc;
        }),
      };
    }

    case UPDATE_SEARCH_PREREQUISITES: {
      return {
        ...state,
        since: payload.since,
        limit: payload.limit,
      };
    }

    case LAST_SEEN_UPDATED: {
      return {
        ...state,
        lastSeen: payload ? [{ ...payload, _selected: true }] : [],
      };
    }

    default:
      return state;
  }
};

const clearSelected = (value) => {
  value._selected = false;
  return value;
};

const reduceFacets = (state, type, payload) => {
  const newState = { ...state, cursor: 0 };
  switch (type) {
    case FACET_SELECTED: {
      const { value, facetType } = payload;
      const { id } = value;

      newState[facetType].forEach((facet) => {
        if (facet.id === id) {
          facet._selected = true;

          if (facetType !== COMMUNICATED_AT_FACET_TYPE) {
            facet.tag = tags[facetType](id);
          }
        }
      });
      break;
    }

    case FACET_SELECTED_EXCLUSIVE: {
      const { value, facetType } = payload;
      const { id } = value;

      newState[facetType].forEach((facet) => {
        facet._selected = facet.id === id;
        if (facet._selected) {
          if (![COMMUNICATED_AT_FACET_TYPE].includes(facetType)) {
            facet.tag = tags[facetType](id);
          }
        }
      });

      break;
    }

    case FACET_DESELECTED: {
      const { value, facetType } = payload;
      const { id } = value;

      newState[facetType].forEach((facet) => {
        if (facet.id === id) {
          facet._selected = false;
        }
      });
      break;
    }

    case FACET_DESELECTED_EXCLUSIVE: {
      const { facetType } = payload;
      newState[facetType].forEach((facet) => {
        facet._selected = false;
      });
      break;
    }

    default:
      return { ...newState };
  }
  return newState;
};

const updateSecurityGroups = (state, payload) => {
  const { securityGroups } = state;

  //re-populate security groups from the payload with respect to user selection
  const updatedSecurityGroups = payload.map((item) => {
    const existingItem = find(securityGroups, (group) => group.id === item.id);
    if (!existingItem) return { ...item, _selected: false };

    return {
      ...existingItem,
      ...item,
      _selected: existingItem._selected,
    };
  });

  return {
    ...state,
    securityGroups: updatedSecurityGroups,
  };
};

const getSelectedIds = (collection = []) => {
  return collection.filter((item) => item._selected).map((selected) => ({ id: selected.id }));
};

const hashCollection = (collection = []) => {
  const selected = getSelectedIds(collection);
  return selected.length > 0 ? hash(JSON.stringify(sortBy(selected, ['id']))) : '';
};

const hashRange = (range) => {
  //make suse to hash only property with value and order(min,max)
  let { min, max } = range || {};
  min = min || undefined;
  max = max || undefined;
  return max || min ? hash(JSON.stringify({ min, max })) : '';
};

export const createSearchHash = (search) => {
  const {
    query,
    alerts,
    maintenance,
    lastCommunicated,
    securityGroups,
    tagFilters,
    accounts,
    lastSeen,
  } = search;

  let searchHash = '';
  //build query hash
  searchHash = (query || []).length > 0 ? hash(JSON.stringify(sortBy(query))) : '';
  //build alerts selected hash
  searchHash += hashCollection(alerts);
  //build maintenance selected hash
  searchHash += hashCollection(maintenance);
  //build lastCommunicated selected hash
  searchHash += hashCollection(lastCommunicated);
  //build securityGroups selected hash
  searchHash += hashCollection(securityGroups);
  //build tagFilters hash
  searchHash += keys(tagFilters).length > 0 ? hash(JSON.stringify(tagFilters)) : '';
  //build accounts selected hash
  searchHash += hashCollection(accounts);
  //hash last seen range
  const [lastSeenFilter] = lastSeen || [];
  searchHash += lastSeenFilter ? hashRange(lastSeenFilter.range) : '';

  return hash(searchHash);
};
